import { z } from "zod"

// Slack caps button text at 75 characters; a shorter cap keeps the email pill on one line.
export const CHANGELOG_BUTTON_LABEL_MAX_LENGTH = 30
export const CHANGELOG_BUTTON_URL_MAX_LENGTH = 2000
export const DEFAULT_CHANGELOG_BUTTON_LABEL = "View changelog"

export const CHANGELOG_BUTTON_MODES = ["post", "custom", "none"] as const
export type ChangelogButtonMode = (typeof CHANGELOG_BUTTON_MODES)[number]

export type ChangelogButtonUrlProblem =
	| "empty"
	| "too-long"
	| "invalid"
	| "not-https"
	| "credentials"
	| "not-public"

export type ChangelogButtonUrlCheck =
	| { ok: true; url: string; host: string; lookalike: boolean }
	| { ok: false; problem: ChangelogButtonUrlProblem }

export const CHANGELOG_BUTTON_URL_MESSAGES: Record<
	ChangelogButtonUrlProblem,
	string
> = {
	empty: "Paste the full https:// link for this post.",
	"too-long": "Keep the link under 2,000 characters.",
	invalid: "Enter the full link, starting with https://.",
	"not-https": "Only https:// links can be sent.",
	credentials: "Remove the login details from the link.",
	"not-public": "Use a public website address.",
}

const PRIVATE_HOST_SUFFIXES = [".localhost", ".local", ".internal"]

function isPublicHostname(host: string) {
	if (!host.includes(".") || host.startsWith("[")) return false
	if (/^\d+(\.\d+){3}$/.test(host)) return false
	return !PRIVATE_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix))
}

// The link goes out to every subscriber and Slack channel, so only a plain
// https page on a public host passes: no javascript:, data:, http: or user:pass@.
export function checkChangelogButtonUrl(
	raw: string | null | undefined,
): ChangelogButtonUrlCheck {
	const value = (raw ?? "").trim()
	if (!value) return { ok: false, problem: "empty" }
	if (value.length > CHANGELOG_BUTTON_URL_MAX_LENGTH) {
		return { ok: false, problem: "too-long" }
	}
	// eslint-disable-next-line no-control-regex
	if (/[\s\u0000-\u001f\u007f]/.test(value)) {
		return { ok: false, problem: "invalid" }
	}

	let parsed: URL
	try {
		parsed = new URL(value)
	} catch {
		return { ok: false, problem: "invalid" }
	}

	if (parsed.protocol !== "https:") return { ok: false, problem: "not-https" }
	if (parsed.username || parsed.password) {
		return { ok: false, problem: "credentials" }
	}

	const host = parsed.hostname.toLowerCase()
	if (!isPublicHostname(host)) return { ok: false, problem: "not-public" }

	return {
		ok: true,
		url: parsed.href,
		host,
		// Punycode labels can spell a lookalike of a familiar domain.
		lookalike: host.split(".").some((label) => label.startsWith("xn--")),
	}
}

export function isOwnChangelogDomain(
	host: string,
	ownDomains: readonly string[],
): boolean {
	const target = host.toLowerCase()
	return ownDomains.some((domain) => {
		const own = domain
			.trim()
			.toLowerCase()
			.replace(/^\.+|\.+$/g, "")
		return !!own && (target === own || target.endsWith(`.${own}`))
	})
}

export function sanitizeChangelogButtonLabel(
	raw: string | null | undefined,
): string {
	return (
		(raw ?? "")
			.replace(/\s+/g, " ")
			// eslint-disable-next-line no-control-regex
			.replace(/[\u0000-\u001f\u007f]/g, "")
			.trim()
			.slice(0, CHANGELOG_BUTTON_LABEL_MAX_LENGTH)
	)
}

const labelSchema = z
	.string()
	.max(
		CHANGELOG_BUTTON_LABEL_MAX_LENGTH,
		`Button text can be at most ${CHANGELOG_BUTTON_LABEL_MAX_LENGTH} characters.`,
	)
	.transform(sanitizeChangelogButtonLabel)

const urlSchema = z
	.string()
	.max(CHANGELOG_BUTTON_URL_MAX_LENGTH)
	.transform((value, ctx) => {
		const check = checkChangelogButtonUrl(value)
		if (!check.ok) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: CHANGELOG_BUTTON_URL_MESSAGES[check.problem],
			})
			return z.NEVER
		}
		return check.url
	})

export const changelogActionButtonSchema = z.discriminatedUnion("mode", [
	z.object({ mode: z.literal("post"), label: labelSchema.optional() }),
	z.object({
		mode: z.literal("custom"),
		label: labelSchema.optional(),
		url: urlSchema,
	}),
	z.object({ mode: z.literal("none") }),
])

export type ChangelogActionButton = z.output<typeof changelogActionButtonSchema>
export type ChangelogActionButtonInput = z.input<
	typeof changelogActionButtonSchema
>

export interface ResolvedChangelogButton {
	label: string
	url: string
}

// No choice (older callers, the public API) keeps the button on the post.
export function resolveChangelogActionButton(
	button: ChangelogActionButtonInput | null | undefined,
	postUrl: string,
): ResolvedChangelogButton | null {
	if (button?.mode === "none") return null

	const label =
		sanitizeChangelogButtonLabel(button?.label) ||
		DEFAULT_CHANGELOG_BUTTON_LABEL

	if (button?.mode === "custom") {
		const check = checkChangelogButtonUrl(button.url)
		if (check.ok) return { label, url: check.url }
	}

	return { label, url: postUrl }
}
